import type { ApiError } from '@/types';

/**
 * Gateway route prefixes for each backend service.
 */
export const SERVICE_PATHS = {
  llmConfig: '/gateway/aiengine/api/admin/llm-config',
  whatsappConfig: '/gateway/comms-service/api/whatsapp/config',
  documents: '/gateway/document-service/api',
  upload: '/gateway/document-service/api/upload',
  messages: '/gateway/comms-service/whatsapp/messages',
} as const;

type TokenGetter = () => string | null;

let _getAuthToken: TokenGetter = () => null;

/**
 * Register a function that returns the current auth token.
 * Called once during app initialization (e.g., in AuthProvider).
 */
export function setAuthTokenGetter(getter: TokenGetter): void {
  _getAuthToken = getter;
}

function getBaseUrl(): string {
  return import.meta.env.VITE_GATEWAY_BASE_URL ?? '';
}

/**
 * Normalize a non-2xx response into a structured ApiError.
 */
async function normalizeError(response: Response): Promise<ApiError> {
  let message = `Request failed with status ${response.status}`;
  let details: Record<string, string[]> | undefined;

  try {
    const body = await response.json();
    if (typeof body.message === 'string') {
      message = body.message;
    }
    if (body.details && typeof body.details === 'object') {
      details = body.details;
    }
  } catch {
    // Response body was not JSON — use the default message
  }

  return { status: response.status, message, details };
}

export interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
}

/**
 * Core fetch wrapper that:
 * 1. Prepends the gateway base URL
 * 2. Attaches Authorization Bearer header
 * 3. Sets Content-Type and Accept headers for JSON payloads
 * 4. Returns parsed JSON or throws a structured ApiError for non-2xx responses
 * 5. Catches network errors and returns ApiError with status 0
 */
export async function apiClient<T>(
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  const { body, headers: customHeaders, ...restOptions } = options;

  const headers = new Headers(customHeaders);

  // Always set Accept header
  if (!headers.has('Accept')) {
    headers.set('Accept', 'application/json');
  }

  // Set Content-Type for JSON payloads (skip for FormData / multipart)
  if (body !== undefined && !(body instanceof FormData)) {
    if (!headers.has('Content-Type')) {
      headers.set('Content-Type', 'application/json');
    }
  }

  // Attach auth token
  const token = _getAuthToken();
  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  const url = `${getBaseUrl()}${path}`;

  let response: Response;
  try {
    response = await fetch(url, {
      ...restOptions,
      headers,
      body: body instanceof FormData ? body : body !== undefined ? JSON.stringify(body) : undefined,
    });
  } catch {
    // Network error — server unreachable
    const networkError: ApiError = {
      status: 0,
      message: 'Cannot connect to the server. Please check your connection and try again.',
    };
    throw networkError;
  }

  if (!response.ok) {
    const error = await normalizeError(response);
    throw error;
  }

  // Handle 204 No Content
  if (response.status === 204) {
    return undefined as T;
  }

  return response.json() as Promise<T>;
}

// --- Convenience methods ---

export function apiGet<T>(path: string, options?: Omit<RequestOptions, 'body'>): Promise<T> {
  return apiClient<T>(path, { ...options, method: 'GET' });
}

export function apiPost<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T> {
  return apiClient<T>(path, { ...options, method: 'POST', body });
}

export function apiPut<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T> {
  return apiClient<T>(path, { ...options, method: 'PUT', body });
}

export function apiDelete<T>(path: string, options?: RequestOptions): Promise<T> {
  return apiClient<T>(path, { ...options, method: 'DELETE' });
}
